import * as React from 'react';
import { observer } from 'mobx-react';
import { Modal } from '../../components/popup';
import { Users } from '../../stores/Users';
import { PortalSearchBarField } from '../../components/layout';
import { EUserType } from '../../../../common/interfaces/user';
import { Picker, Autocomplete, IAutocompleteSuggestion } from '../../components/forms';
import { EUserPortalPermissionFilter, IAnyUser, EUserTypeFilter } from '../../../../common/interfaces/users';
import { EStatus, EUserSaveStatus } from './UserModal';
import { StatusIconWithLabel, EStatusIconType, SavingIndicator, SavingIndicatorStatus } from '../../components/indicator';
import { Button, EButtonType } from '../../components/action';

// Styles
const s = require('./UserModal.css');

// Props
interface IUserModalViewProps {
    users: Users;
    user: IAnyUser | null;
    userIsNew: boolean;
    userError: string | null;
    userPassword: string;
    userPasswordRepeat: string;
    showUserTypePicker: boolean;
    showPermissionPicker: boolean;
    showStatusPicker: boolean;
    userIsModified: boolean;
    userPasswordResetStatus: EStatus;
    userSaveStatus: EUserSaveStatus;
    onModalClose: () => void;
    onModalDisabledClose: () => void;
    onModalContainerClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    onFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    onUserPortalPermissionsPickerClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    onUserPortalPermissionChange: (newValue: any) => (e: React.MouseEvent<HTMLButtonElement>) => void;
    onUserPortalPermissionFieldFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
    onUserTypePickerClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    onUserTypeChange: (newValue: any) => (e: React.MouseEvent<HTMLButtonElement>) => void;
    onUserTypeFieldFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
    onUserStatusPickerClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    onUserStatusChange: (newValue: any) => (e: React.MouseEvent<HTMLButtonElement>) => void;
    onUserStatusFieldFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
    onTextFieldChange: (field: string, value: string) => void;
    onPasswordFieldChange: (field: string, value: string) => void;
    onPasswordReset: (e: React.MouseEvent<HTMLButtonElement>) => void;
    referenceCustomerCardCodeFieldGroup: (ref: PortalSearchBarField) => void;
    onCustomerCardCodeFieldClick: (field: string, e: React.MouseEvent<HTMLLabelElement>) => void;
    onCustomerCardCodeFocus: (field: string, e: React.FocusEvent<HTMLInputElement>) => void;
    showCustomerCardCodeAutocomplete: boolean;
    customerCardCodeAutocompleteStyle: React.CSSProperties | null;
    onCustomerCardCodeAutocompleteDismiss: () => void;
    onCustomerCardCodeSuggestionsClick: (suggestion: IAutocompleteSuggestion, e: React.MouseEvent<HTMLButtonElement>) => void;
    onCustomerCardCodeLoadMoreSuggestionsClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

// Component
export const UserModalView = observer((props: IUserModalViewProps) => (
    <Modal
        onClose={props.onModalClose}
        onDisabledClose={props.onModalDisabledClose}
        onModalContainerClick={props.onModalContainerClick}
        disableClose={props.userIsModified}
        show={props.users.userModalIsOpen}
        title={props.userIsNew ? 'Create new user' : 'Edit user ' + props.users.userModalUserDetails.username}
    >
        <form className={s.form} onSubmit={props.onFormSubmit}>
            <div className={s.section}>
                <h5>Login</h5>
                <div>
                    <PortalSearchBarField
                        lightness={4}
                        label="Username"
                        field="username"
                        readOnly={props.user.id !== null}
                        value={props.user.username}
                        onChange={props.onTextFieldChange}
                    />

                    {props.user.typeId === EUserType.Portal && (
                        <Picker
                            onContainerClick={props.onUserPortalPermissionsPickerClick}
                            show={props.showPermissionPicker}
                            valueSelected={props.user.portalPermissionId}
                            onValueSelectedChange={props.onUserPortalPermissionChange}
                            field={{
                                onFocus: props.onUserPortalPermissionFieldFocus,
                                label: 'User portal permissions',
                                value:
                                    props.user.portalPermissionId &&
                                    typeof Object.keys(EUserPortalPermissionFilter)[props.user.portalPermissionId] !==
                                        'undefined'
                                        ? EUserPortalPermissionFilter[
                                              Object.keys(EUserPortalPermissionFilter)[props.user.portalPermissionId]
                                          ]
                                        : 'None selected'
                            }}
                            options={[
                                {
                                    name: 'User portal permissions',
                                    options: [
                                        ...(props.user.portalPermissionId === null || props.user.portalPermissionId === 0
                                            ? [
                                                  {
                                                      key: 'none',
                                                      title: 'None',
                                                      subline: 'No user permissions selected',
                                                      value: null,
                                                      dim: true
                                                  }
                                              ]
                                            : []),
                                        ...[
                                            {
                                                key: 'stock-only',
                                                title: 'Items stock',
                                                subline: 'Access to search and items stock',
                                                value: 1
                                            },
                                            {
                                                key: 'stock-and-pricing',
                                                title: 'Items stock and price',
                                                subline: 'Access to search, items stock and price',
                                                value: 2
                                            },
                                            {
                                                key: 'stock-pricing-ordering',
                                                title: 'Items stock, price and ordering',
                                                subline: 'Access to all above with ability to order',
                                                value: 3
                                            }
                                        ]
                                    ]
                                }
                            ]}
                        />
                    )}

                    <Picker
                        onContainerClick={props.onUserTypePickerClick}
                        show={props.showUserTypePicker}
                        valueSelected={props.user.typeId}
                        onValueSelectedChange={props.onUserTypeChange}
                        field={{
                            onFocus: props.onUserTypeFieldFocus,
                            label: 'User type',
                            value:
                                props.user.typeId && Object.keys(EUserTypeFilter)[props.user.typeId] !== 'undefined'
                                    ? EUserTypeFilter[Object.keys(EUserTypeFilter)[props.user.typeId]]
                                    : 'None selected'
                        }}
                        options={[
                            {
                                name: 'User type',
                                options: [
                                    ...(props.user.typeId === null || props.user.typeId === 0
                                        ? [
                                              {
                                                  key: 'none',
                                                  title: 'None',
                                                  subline: 'No user type selected',
                                                  value: null,
                                                  dim: true
                                              }
                                          ]
                                        : []),
                                    ...[
                                        {
                                            key: 'admin',
                                            title: 'Administrator',
                                            subline: 'Access to portal settings and users',
                                            value: 1
                                        },
                                        {
                                            key: 'portal',
                                            title: 'Customer',
                                            subline: 'Access to parts search',
                                            value: 2
                                        },
                                        {
                                            key: 'internal',
                                            title: 'Internal',
                                            subline: 'Access to CSV uploads',
                                            value: 3
                                        }
                                    ]
                                ]
                            }
                        ]}
                    />
                </div>
            </div>
            <div className={s.section}>
                <h5>Details</h5>
                <div>
                    <PortalSearchBarField
                        lightness={4}
                        label="Full name"
                        field="name"
                        value={props.user.name}
                        onChange={props.onTextFieldChange}
                    />

                    {props.user.isCustomer && (
                        <PortalSearchBarField
                            ref={props.referenceCustomerCardCodeFieldGroup}
                            lightness={4}
                            label="Customer card code"
                            field="cardcode"
                            value={props.user.cardcode}
                            onChange={props.onTextFieldChange}
                            onClick={props.onCustomerCardCodeFieldClick}
                            onFocus={props.onCustomerCardCodeFocus}
                        />
                    )}

                    <Autocomplete
                        show={
                            props.showCustomerCardCodeAutocomplete &&
                            (props.users.customerCardCodeSuggestionsTotal > 0 || props.users.customerCardCodeSuggestionsLoading)
                        }
                        style={props.customerCardCodeAutocompleteStyle}
                        showLoadMore={
                            props.users.customerCardCodeSuggestionsLoading === false &&
                            props.users.customerCardCodeSuggestions &&
                            props.users.customerCardCodeSuggestionsTotal > props.users.customerCardCodeSuggestionsLimit
                        }
                        loading={props.users.customerCardCodeSuggestionsLoading}
                        suggestionsLabel="Customers list"
                        suggestionsCloseLabel="Dismiss"
                        suggestions={props.users.customerCardCodeSuggestions.map(suggestion => ({
                            value: suggestion.cardcode,
                            label: `${suggestion.cardcode}${suggestion.cardname ? ' - ' + suggestion.cardname : ''}`
                        }))}
                        onDismissClick={props.onCustomerCardCodeAutocompleteDismiss}
                        onLoadMoreClick={props.onCustomerCardCodeLoadMoreSuggestionsClick}
                        onSuggestionClick={props.onCustomerCardCodeSuggestionsClick}
                    />
                </div>
            </div>
            <div className={s.section}>
                <h5>Contact</h5>
                <div>
                    <PortalSearchBarField
                        lightness={4}
                        label="Email address"
                        field="email"
                        value={props.user.email}
                        onChange={props.onTextFieldChange}
                    />

                    {props.userIsNew === false && (
                        <StatusIconWithLabel
                            className={s.emailConfirmed}
                            label={props.user.emailConfirmed ? 'Email address confirmed' : 'Email address not verified'}
                            type={
                                props.userIsNew
                                    ? EStatusIconType.Dim
                                    : props.user.emailConfirmed ? EStatusIconType.Success : EStatusIconType.Error
                            }
                        />
                    )}
                </div>
            </div>
            {props.userIsNew && (
                <div className={s.section}>
                    <h5>Credentials</h5>
                    <div>
                        <PortalSearchBarField
                            lightness={4}
                            type="password"
                            label={`${props.userIsNew ? 'New' : 'Change'} user password`}
                            field="userPassword"
                            value={props.userPassword}
                            onChange={props.onPasswordFieldChange}
                        />

                        <PortalSearchBarField
                            lightness={4}
                            type="password"
                            label="Repeat new user password"
                            field="userPasswordRepeat"
                            value={props.userPasswordRepeat}
                            onChange={props.onPasswordFieldChange}
                        />
                    </div>
                </div>
            )}
            <div className={s.section}>
                <h5>Summary</h5>
                <div className={s.summary}>
                    <div className={s.left}>
                        <Picker
                            onContainerClick={props.onUserStatusPickerClick}
                            show={props.showStatusPicker}
                            valueSelected={props.user.isActive}
                            onValueSelectedChange={props.onUserStatusChange}
                            field={{
                                onFocus: props.onUserStatusFieldFocus,
                                label: 'User status',
                                value: props.user.isActive ? 'Active' : 'Disabled'
                            }}
                            options={[
                                {
                                    name: 'User status',
                                    options: [
                                        {
                                            key: 'disabled',
                                            title: 'Disabled',
                                            subline: 'User will not be able to login',
                                            value: false
                                        },
                                        {
                                            key: 'enabled',
                                            title: 'Enabled',
                                            subline: 'User will have access based on his type',
                                            value: true
                                        }
                                    ]
                                }
                            ]}
                        />

                        {props.userIsNew === false && (
                            <Button
                                className={s.resetPasswordButton}
                                onClick={props.onPasswordReset}
                                inBox={true}
                                type={
                                    props.userPasswordResetStatus === EStatus.Success
                                        ? EButtonType.Success
                                        : props.userPasswordResetStatus === EStatus.Error
                                          ? EButtonType.Important
                                          : EButtonType.Default
                                }
                            >
                                {props.userPasswordResetStatus === EStatus.Saving
                                    ? 'Resetting...'
                                    : props.userPasswordResetStatus === EStatus.Success
                                      ? 'Password reset'
                                      : props.userPasswordResetStatus === EStatus.Error
                                        ? 'Could not reset, try again'
                                        : 'Reset password'}
                            </Button>
                        )}
                    </div>
                    <div className={s.right}>
                        <Button inBox={true} type={props.userError === null ? EButtonType.Action : EButtonType.BorderOnly}>
                            {props.userIsNew ? 'Create new user' : 'Save changes'}
                        </Button>
                        <SavingIndicator
                            className={s.saveStatus}
                            labels={{
                                error: props.userError
                                    ? props.userError
                                    : props.userSaveStatus === EUserSaveStatus.Error
                                      ? 'Could not save, try again'
                                      : props.userSaveStatus === EUserSaveStatus.ErrorCardCodeDoesNotExist
                                        ? 'Card code does not exist, enter existing customer'
                                        : '',
                                saving: props.userIsNew ? 'Creating user' : 'Saving user changes',
                                success: props.userIsNew ? 'User created' : 'User updated'
                            }}
                            status={
                                props.userSaveStatus === EUserSaveStatus.Saving
                                    ? SavingIndicatorStatus.Saving
                                    : props.userSaveStatus === EUserSaveStatus.Success
                                      ? SavingIndicatorStatus.Success
                                      : [EUserSaveStatus.Error, EUserSaveStatus.ErrorCardCodeDoesNotExist].indexOf(
                                            props.userSaveStatus
                                        ) !== -1 || props.userError
                                        ? SavingIndicatorStatus.Error
                                        : SavingIndicatorStatus.None
                            }
                        />
                    </div>
                </div>
            </div>
        </form>
    </Modal>
));



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/admin/UserModalView.tsx